<template>
    <div class="row" id="payprop-upload">
        <div class="col-sm-12" v-if="mutableHasQueuedImports">
            <div class="alert alert-info" role="alert">
                Please note there is a Queued Upload for your team. Please wait for that process to complete before uploading new files. Large files (1000 rows+) can
                take up to 30 minutes to complete.
            </div>
        </div>

        <div class="col-sm-12" v-if="errorMessage">
            <div class="alert alert-danger" role="alert">
                <h4 class="alert-heading">Error!</h4>

                <template v-if="Array.isArray(errorMessage)">
                    <ul>
                        <li v-for="(message, index) in errorMessage" :key="index">
                            {{ message }}
                        </li>
                    </ul>
                </template>
                <template v-else>
                    <p>{{ errorMessage }}</p>

                    <template v-if="missingFields.length > 0">
                        <hr>
                        <p class="mb-0">Missing Fields:</p>
                        <ul>
                            <li v-for="(missingField, index) in missingFields" :key="index">
                                {{ missingField }}
                            </li>
                        </ul>
                    </template>
                </template>
            </div>
        </div>

        <div class="col-sm-12">
            <!-- Tenants Dropzone-->
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-sm-12 col-md-4">
                            <el-upload
                                class="payprop-files-drop-zone"
                                drag
                                ref="tenantsPayProp"
                                action="https://jsonplaceholder.typicode.com/posts/"
                                :multiple="false"
                                :auto-upload="false"
                                :limit="1"
                                :accept="acceptFiles"
                                :on-remove="deleteFile('tenants_file')"
                            >
                                <i class="el-icon-upload"></i>
                                <div class="el-upload__text">
                                    Drop your <span class="font-bold text-indigo-600">tenants_active.csv</span> file here or
                                    <div>
                                        <em>Click to upload</em>
                                    </div>
                                </div>
                            </el-upload>
                        </div>

                        <div class="col-sm-12 col-md-8 my-3">
                            <h4 class="card-title">PayProp Property & Tenants Data Import Instructions</h4>

                            <div class="instructions">
                                <ol>
                                    <li>Go to the reports page in PayProp and download the <span class="font-bold">Active Tenants</span> File, <br>
                                        or the <span class="font-bold">All Tenants</span> file if it has less than 5000 records, <br>
                                        as <span class="font-bold">CSV</span>.
                                    </li>
                                    <li><strong>Do not open</strong> the file or make any changes to it.</li>
                                    <li>Drop this file here. We'll use this file to create / update your properties & tenants</li>
                                </ol>

                                <img src="/images/csv-upload-1.png" alt="..." class="dzopzone-images dzopzone-images-1">
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Beneficiaries Dropzone -->
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-sm-12 col-md-4">
                            <el-upload
                                Mond class="payprop-files-drop-zone"
                                drag
                                ref="beneficiariesPayProp"
                                action="https://jsonplaceholder.typicode.com/posts/"
                                :multiple="false"
                                :auto-upload="false"
                                :limit="1"
                                :accept="acceptFiles"
                                :on-remove="deleteFile('beneficiaries_file')"
                            >
                                <i class="el-icon-upload"></i>
                                <div class="el-upload__text">
                                    Drop your <span class="font-bold text-indigo-600">beneficiaries_owners.csv</span> file here or
                                    <div>
                                        <em>Click to upload</em>
                                    </div>
                                </div>
                            </el-upload>
                        </div>

                        <div class="col-sm-12 col-md-8 my-3">
                            <h4 class="card-title">PayProp Landlord Data Import Instructions</h4>
                            <div class="instructions">
                                <ol>
                                    <li>Go to the reports page in PayProp and download the <span class="font-bold">"Beneficiaries Owners"</span> File, <br>
                                        or the <span class="font-bold">"All Beneficiaries"</span> file if it has less than 5000 records, <br>
                                        as <span class="font-bold">CSV</span>.
                                    </li>
                                    <li>Do not open the file or make any changes to it.</li>
                                    <li>Drop this file here. We'll use this file to create/update the landlords for your properties</li>
                                </ol>

                                <img src="/images/csv-upload-2.png" alt="..." class="dzopzone-images dzopzone-images-2">
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="text-right">
                <button :disabled="disableImportButton || mutableHasQueuedImports"
                        class="select-none sm:ml-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow text-white bg-indigo-600 hover:bg-indigo-700"
                        @click.prevent="uploadFiles">
                    {{ form.busy ? "Uploading" : "import" }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>

export default {
    name: "payprop-files-upload",
    props: {
        hasQueuedImports: {
            type: Boolean,
            default: false,
            description: "User active queued import batches"
        }
    },
    data () {
        return {
            form: new FormData(),
            errorMessage: null,
            missingFields: [],
            mutableHasQueuedImports: _.cloneDeep(this.hasQueuedImports),
            disableImportButton: false,
            acceptFiles: ".csv"
        };
    },
    methods: {
        checkFormDataFiles () {
            return !this.form.has("tenants_file") && !this.form.has("beneficiaries_file");
        },
        addFile (name, file) {
            this.form.append(name, file);
        },
        deleteFile (name) {
            this.form.delete(name);
        },
        clearForm () {
            let fields = ["tenants_file", "beneficiaries_file"];

            for (let i = 0; i < fields.length; i++) {
                this.deleteFile(fields[i]);
            }
        },
        uploadFiles () {
            this.form.busy = true;

            if (typeof this.$refs.tenantsPayProp.uploadFiles[0] !== "undefined")
                this.addFile("tenants_file", this.$refs.tenantsPayProp.uploadFiles[0].raw);

            if (typeof this.$refs.beneficiariesPayProp.uploadFiles[0] !== "undefined")
                this.addFile("beneficiaries_file", this.$refs.beneficiariesPayProp.uploadFiles[0].raw);

            if (this.checkFormDataFiles()) {
                return false;
            }

            this.disableImportButton = true;
            this.errorMessage = null;
            this.missingFields = [];

            axios.post("payprop-files/upload", this.form)
            .then(response => {

                if (!_.isUndefined(response)) {
                    this.clearForm();

                    this.mutableHasQueuedImports = true;
                    this.disableImportButton = false;

                    this.$refs.tenantsPayProp.clearFiles();
                    this.$refs.beneficiariesPayProp.clearFiles();

                    swal.fire(
                        response.data.title,
                        response.data.message,
                        "success"
                    );
                }
                this.form.busy = false;
                this.mutableHasQueuedImports = false;
            }).catch(error => {

                this.disableImportButton = false;
                let errorResponse = error.response;

                if (errorResponse.status === 422 && typeof errorResponse.data.message !== "undefined") {

                    // Laravel validation error - If file is incorrect, not csv
                    if (typeof errorResponse.data.errors !== "undefined") {
                        this.errorMessage = [];

                        for (let [key, value] of Object.entries(errorResponse.data.errors)) {
                            value.forEach(errorMessage => {
                                this.errorMessage.push(errorMessage);
                            });

                            if ("tenants_file")
                                this.$refs.tenantsPayProp.clearFiles();

                            if ("beneficiaries_file")
                                this.$refs.beneficiariesPayProp.clearFiles();

                            this.deleteFile(key);
                        }
                    }
                    // If csv File is incorrect
                    else {
                        this.errorMessage = errorResponse.data.error + " " + errorResponse.data.message;

                        if (typeof errorResponse.data.missing_fields !== "undefined") {
                            this.missingFields = errorResponse.data.missing_fields;
                        }
                    }
                }

                this.form.busy = false;
                toastr.error("Error occurred on file(s) upload");
            });
        }
    }
};
</script>

<style scoped>
.btn-primary:disabled {
    cursor: no-drop;
}
</style>
